import { useCallback, useEffect, useRef, useState } from "react";
import { flushOperatorDraftWrites } from "./operator-draft-sync";

const assetPattern = /^\/assets\/index-[A-Za-z0-9_-]+\.js$/;
export function buildEntryAssetFromHtml(html: string, origin: string) {
  const scripts = html.match(/<script\b[^>]*>/gi) ?? [];
  for (const script of scripts) {
    if (!/\btype=["']module["']/i.test(script)) continue;
    const source = /\bsrc=["']([^"']+)["']/i.exec(script)?.[1];
    if (!source) continue;
    try {
      const url = new URL(source, origin);
      if (url.origin === origin && assetPattern.test(url.pathname)) return url.pathname;
    } catch { /* Continue past malformed markup. */ }
  }
  return null;
}
export function loadedBuildEntryAsset() {
  const scripts = [...document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')];
  for (const script of scripts) {
    const path = new URL(script.src, window.location.origin).pathname;
    if (assetPattern.test(path)) return path;
  }
  return null;
}
export function useTabletBuildRefresh(blocked: boolean) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const blockedRef = useRef(blocked);
  const refreshingRef = useRef(false);
  blockedRef.current = blocked;
  const check = useCallback(async () => {
    if (document.visibilityState !== "visible" || request.current) return;
    const loaded = loadedBuildEntryAsset();
    if (!loaded) return;
    const controller = new AbortController(); request.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch("/", { cache: "no-store", credentials: "same-origin", headers: { Accept: "text/html" }, signal: controller.signal });
      const current = response.ok ? buildEntryAssetFromHtml(await response.text(), window.location.origin) : null;
      if (current && current !== loaded) setUpdateAvailable(true);
    } catch { /* A version check never alters the operator flow when offline. */ }
    finally { window.clearTimeout(timeout); if (request.current === controller) request.current = null; }
  }, []);
  useEffect(() => {
    mountedRef.current = true;
    void check();
    const interval = window.setInterval(() => void check(), 300_000);
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { mountedRef.current = false; request.current?.abort(); window.clearInterval(interval); document.removeEventListener("visibilitychange", onVisible); };
  }, [check]);
  const refresh = useCallback(async () => {
    if (blockedRef.current || refreshingRef.current) return false;
    refreshingRef.current = true;
    setRefreshing(true); setRefreshError(null);
    try {
      if (!await flushOperatorDraftWrites() || blockedRef.current || !mountedRef.current) { setRefreshError("Cihazdaki taslak kaydedilemedi veya yeni bir işlem başladı. Formu açıp taslağı kontrol edin; güncelleme yapılmadı."); return false; }
      window.location.reload();
      return true;
    } finally { refreshingRef.current = false; setRefreshing(false); }
  }, []);
  return { updateAvailable, refreshing, refresh, blocked, refreshError };
}
